import React from "react"
import useSearchContext from "./use-search-context"
import { useSearchSelector } from "./search-redux-context"
import { UseSearchReturn } from "./use-search"

type ValueUpdater = (prev: string[]) => string[]

export const filterValueUpdaters = {
  add:
    (v: string): ValueUpdater =>
    (prev) => [v, ...prev],
  remove:
    (v: string): ValueUpdater =>
    (prev) =>
      prev.filter((x) => x !== v),
  toggle:
    (v: string): ValueUpdater =>
    (prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [v, ...prev],
  clear: ((_prev: string[]) => []) as ValueUpdater,
}

export type UseFilterReturn = {
  isRegistered: boolean
  values: any[]
  setValues(values: any[]): void
  initialiseValues(values: any[]): void
  toggleValue(v: string): void
  addValue(v: string): void
  removeValue(v: string): void
  clearValues(): void
  search(): void
}

export default function useFilter(
  filterId: string,
  searchMethods?: UseSearchReturn,
): UseFilterReturn {
  const { filters, search } = useSearchContext(searchMethods)
  const values = useSearchSelector(filters.valuesSelector(filterId))
  const isRegistered = useSearchSelector(filters.registeredSelector(filterId))

  const setValues = React.useCallback(
    (values: any[]) => filters.setValues(filterId, values),
    [filters, filterId],
  )
  const initialiseValues = React.useCallback(
    (values: any[]) => filters.initialiseValues(filterId, values),
    [filters, filterId],
  )
  const updateValues = React.useCallback(
    (updater: ValueUpdater) => filters.updateValues(filterId, updater),
    [filters, filterId],
  )
  const toggleValue = React.useCallback(
    (v: string) => updateValues(filterValueUpdaters.toggle(v)),
    [updateValues],
  )
  const addValue = React.useCallback(
    (v: string) => updateValues(filterValueUpdaters.add(v)),
    [updateValues],
  )
  const removeValue = React.useCallback(
    (v: string) => updateValues(filterValueUpdaters.remove(v)),
    [updateValues],
  )
  const clearValues = React.useCallback(
    () => updateValues(filterValueUpdaters.clear),
    [updateValues],
  )

  return React.useMemo(
    () => ({
      isRegistered,
      values,
      initialiseValues,
      setValues,
      toggleValue,
      addValue,
      removeValue,
      clearValues,
      search,
    }),
    [
      isRegistered,
      values,
      setValues,
      initialiseValues,
      toggleValue,
      addValue,
      removeValue,
      clearValues,
      search,
    ],
  )
}
